// Chat client configuration
const CONFIG = {
  // Backend API endpoints
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  TOKEN_ENDPOINT: '/customer-service/token',
  CHAT_WEBSOCKET_ENDPOINT: '/customer-service/chat',
  
  // Reconnection parameters
  MAX_RECONNECT_ATTEMPTS: 5,
  RECONNECT_DELAY_MS: 3000
};

class ChatService {
  constructor(callbacks, masterId) {
    this.socket = null;
    this.token = null;
    this.isConnected = false;
    this.isReceivingMessage = false;
    this.callbacks = callbacks || {};
    this.masterId = masterId;
    // Reconnection state
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
  }

  // Initialize the chat client
  async init() {
    try {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('トークンを取得中...');
      }
      
      // Get access token
      this.token = await this.getAccessToken();
      
      if (!this.token) {
        if (this.callbacks.onStatusChange) {
          this.callbacks.onStatusChange('トークンの取得に失敗しました。再読み込みしてください。', true);
        }
        return false;
      }
      
      // Connect to WebSocket
      return this.connectWebSocket();
      
    } catch (error) {
      console.error('Chat initialization error:', error);
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('チャットの初期化に失敗しました。再読み込みしてください。', true);
      }
      return false;
    }
  }

  // Get access token from the backend
  async getAccessToken() {
    if (!this.masterId) {
      console.error('master_id is required but not provided');
      return null;
    }
    
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}${CONFIG.TOKEN_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          master_id: this.masterId
        }),
      });
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.data && data.data.access_token) {
        return data.data.access_token;
      } else {
        console.error('Failed to get token:', data);
        return null;
      }
    } catch (error) {
      console.error('Token request error:', error);
      return null;
    }
  }

  // Connect to the WebSocket
  connectWebSocket() {
    if (!this.token) {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('トークンがありません。再読み込みしてください。', true);
      }
      return false;
    }
    
    if (this.callbacks.onStatusChange) {
      this.callbacks.onStatusChange('WebSocketに接続中...');
    }
    
    // Close existing connection if any
    if (this.socket) {
      this.socket.close();
    }
    
    // Create new WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = CONFIG.API_BASE_URL || `${protocol}//${window.location.hostname}:8000`;
    const wsProtocol = baseUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsBaseUrl = baseUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsBaseUrl}${CONFIG.CHAT_WEBSOCKET_ENDPOINT}?token=${this.token}`;
    this.socket = new WebSocket(wsUrl);
    
    // WebSocket event handlers
    this.socket.onopen = this.handleSocketOpen.bind(this);
    this.socket.onmessage = this.handleSocketMessage.bind(this);
    this.socket.onclose = this.handleSocketClose.bind(this);
    this.socket.onerror = this.handleSocketError.bind(this);
    
    return true;
  }

  // Handle WebSocket open event
  handleSocketOpen() {
    this.isConnected = true;
    // Reset reconnection attempts on successful connection
    this.reconnectAttempts = 0;
    
    if (this.callbacks.onStatusChange) {
      this.callbacks.onStatusChange('接続しました');
    }
    
    if (this.callbacks.onConnectionChange) {
      this.callbacks.onConnectionChange(true);
    }
  }

  // Handle WebSocket message event
  handleSocketMessage(event) {
    const message = event.data;
    
    // Check for end marker
    if (message === ':end') {
      this.isReceivingMessage = false;
      
      if (this.callbacks.onTypingChange) {
        this.callbacks.onTypingChange(false);
      }
      
      return;
    }
    
    // Check for error messages
    if (message.startsWith('Error:') || message.startsWith('エラー：')) {
      if (this.callbacks.onSystemMessage) {
        this.callbacks.onSystemMessage(message);
      }
      return;
    }
    
    // First message in a response
    if (!this.isReceivingMessage) {
      this.isReceivingMessage = true;
      
      if (this.callbacks.onAssistantMessage) {
        this.callbacks.onAssistantMessage(message);
      }
      
      return;
    }
    
    // Append to the last assistant message
    if (this.callbacks.onAssistantMessageUpdate) {
      this.callbacks.onAssistantMessageUpdate(message);
    }
  }

  // Handle WebSocket close event
  handleSocketClose(event) {
    this.isConnected = false;
    
    if (this.callbacks.onConnectionChange) {
      this.callbacks.onConnectionChange(false);
    }
    
    if (event.wasClean) {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange(`接続が閉じられました (コード: ${event.code})`);
      }
    } else {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('接続が切断されました。再接続を試みています...', true);
      }
      
      // Attempt to reconnect on unexpected disconnection
      this.reconnect();
    }
    
    if (this.callbacks.onSystemMessage) {
      this.callbacks.onSystemMessage('チャットが切断されました。再接続を試みています...');
    }
  }

  // Handle WebSocket error event
  handleSocketError(error) {
    console.error('WebSocket error:', error);
    
    if (this.callbacks.onStatusChange) {
      this.callbacks.onStatusChange('WebSocket接続エラー。再接続を試みています...', true);
    }
    
    // Attempt to reconnect on error
    this.reconnect();
  }

  // Send message to the server
  sendMessage(message) {
    if (!this.isConnected || !this.socket) {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('接続されていません', true);
      }
      return false;
    }
    
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage) {
      return false;
    }
    
    // Send message to server
    this.socket.send(JSON.stringify({ message: trimmedMessage }));
    
    if (this.callbacks.onTypingChange) {
      this.callbacks.onTypingChange(true);
    }
    
    return true;
  }

  // Disconnect WebSocket
  disconnect() {
    // Clear any reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
  }

  // Attempt to reconnect to the WebSocket
  reconnect() {
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Check if max reconnect attempts reached
    if (this.reconnectAttempts >= CONFIG.MAX_RECONNECT_ATTEMPTS) {
      if (this.callbacks.onStatusChange) {
        this.callbacks.onStatusChange('再接続の最大試行回数に達しました。ページを再読み込みしてください。', true);
      }
      if (this.callbacks.onSystemMessage) {
        this.callbacks.onSystemMessage('チャットの再接続に失敗しました。ページを再読み込みしてください。');
      }
      return;
    }
    
    // Increment reconnect attempts
    this.reconnectAttempts++;
    
    if (this.callbacks.onStatusChange) {
      this.callbacks.onStatusChange(`再接続を試みています... (${this.reconnectAttempts}/${CONFIG.MAX_RECONNECT_ATTEMPTS})`);
    }
    
    // Schedule reconnect attempt
    this.reconnectTimer = setTimeout(() => {
      this.connectWebSocket();
    }, CONFIG.RECONNECT_DELAY_MS);
  }

}

export default ChatService;
export { CONFIG };
